import React, { useEffect } from "react";
import { faArrowLeft } from "@fortawesome/free-solid-svg-icons";
import { Box } from "@material-ui/core";
import { withTranslation, WithTranslation } from "react-i18next";
import { connect } from "react-redux";
import { RouteComponentProps, withRouter } from "react-router";
import { Dispatch } from "redux";
import RootState from "../../../redux/RootState";
import { DASHBOARD_ROUTE } from "../../../routes/routes";
import DashboardLayout from "../../../ui/components/DashboardLayout/DashboardLayout";
import ErrorDisplay from "../../../ui/components/ErrorDisplay";
import UserDetailsLoadingPanel from "./components/UserDetailsLoadingPanel";
import UserPanel from "./components/UserPanel";
import ChangePassword from "./model/ChangePassword";
import CurrentUserDetails from "./model/CurrentUserDetails";
import BenchmarkAccount from "./model/BenchmarkAccount";
import {
  currentUserDetailsUpdated,
  loadCurrentUser,
  currentUserChangePassword,
  unlinkBenchmarkEngineer,
  benchmarkEngineerLinked,
} from "./redux/CurrentUserAction";
import CurrentUserState from "./redux/CurrentUserState";

interface ReduxDispatchProps {
  loadCurrentUser: () => void;
  onUserUpdated: (user: CurrentUserDetails) => void;
  onPasswordChanged: (password: ChangePassword, message: string) => void;
  onBenchmarkAction: (action: BenchmarkAccount) => void;
  setBenchmarkLinkSuccess: (user: CurrentUserDetails) => void;
}

interface ReduxStateProps extends CurrentUserState {
  currentOrganisationCountry?: string;
}

type Props = RouteComponentProps & WithTranslation & ReduxDispatchProps & ReduxStateProps;

const AccountSettingsPage: React.FC<Props> = ({
  t,
  user,
  error,
  isLoading,
  success,
  loadCurrentUser,
  onPasswordChanged,
  onUserUpdated,
  onBenchmarkAction,
  setBenchmarkLinkSuccess,
  currentOrganisationCountry,
  history,
}) => {
  const [benchmarkLinkError, setBenchmarkLinkError] = React.useState<string | undefined>(undefined);

  useEffect(() => {
    const savedError = sessionStorage.getItem('benchmark_link_error');
    if (savedError) {
      setBenchmarkLinkError(savedError);
      sessionStorage.removeItem('benchmark_link_error');
    }
    
    loadCurrentUser();
  }, [loadCurrentUser]);

  useEffect(() => {
    const benchmarkLinked = sessionStorage.getItem('benchmark_link_success');
    
    if (benchmarkLinked === 'true' && user) {
      setBenchmarkLinkSuccess(user);
      sessionStorage.removeItem('benchmark_link_success');
    }
  }, [user, setBenchmarkLinkSuccess]);

  const handleNavLinkClicked = (_: string) => {
    history.replace(DASHBOARD_ROUTE);
  };

  const renderBody = () => {
    if (user) {
      const links = [
        {
          key: "dashboard",
          name: t("accountSettingsPage.userSettingsLink"),
          startIcon: faArrowLeft,
          onClick: handleNavLinkClicked,
        },
      ];

      return (
        <Box>
          <UserPanel
            pageTitle={t("accountSettingsPage.userSettingsTitle")}
            navLinks={links}
            onPasswordChanged={onPasswordChanged}
            onUserUpdated={onUserUpdated}
            onBenchmarkAction={onBenchmarkAction}
            user={user}
            error={benchmarkLinkError || error}
            loading={isLoading}
            success={success}
            userOrganisationCountry={currentOrganisationCountry}
          />
        </Box>
      );
    } else if (error) {
      return <ErrorDisplay title={t("accountSettingsPage.errorTitle")} message={error} />;
    } else {
      return <UserDetailsLoadingPanel />;
    }
  };

  return <DashboardLayout>{renderBody()}</DashboardLayout>;
};

const mapStateToProps = (state: RootState): ReduxStateProps => {
  const orgCountry = state.activeOrganisation?.currentOrganisation?.address_country?.code;
 
  return {
    ...state.currentUser,
    currentOrganisationCountry: orgCountry,
  };
};

const mapDispatchToProps = (dispatch: Dispatch): ReduxDispatchProps => ({
  loadCurrentUser: () => dispatch(loadCurrentUser()),
  onPasswordChanged: (password, message) => dispatch(currentUserChangePassword(password, message)),
  onUserUpdated: (currentUser) => dispatch(currentUserDetailsUpdated(currentUser)),
  setBenchmarkLinkSuccess: (user) => dispatch(benchmarkEngineerLinked(user)),
  onBenchmarkAction: (action: BenchmarkAccount) => {
    if (action.type === "unlink") {
      dispatch(unlinkBenchmarkEngineer());
    }
  },
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withTranslation()(withRouter(AccountSettingsPage)));
